import React from 'react';
import { TextField } from '@mui/material';
import { SentenceApi } from '../api/SentenceApi';

/** Suggestions are drafts. Never replace an existing reading on editor open,
 * or overwrite typing with a late response for this or an older Chinese value.
 * A reading belongs to its text: when the Chinese changes the old reading goes at once, and ``onPendingChange`` says
 * a suggestion is on its way, so an editor can wait for it rather than save the new text with no reading. A suggestion
 * for text that did not change holds nothing up: it was saved without a reading, and saving it again loses nothing. */
export function VerbaScholaPinyinField({ hanzi, value, onChange, chineseOnly = false, onPendingChange }: {
    hanzi: string; value: string; chineseOnly?: boolean; onChange: (value: string) => void; onPendingChange?: (pending: boolean) => void;
}) {
    const latest = React.useRef({ hanzi, value, onChange, onPendingChange });
    latest.current = { hanzi, value, onChange, onPendingChange };
    const suggestionText = chineseOnly ? (hanzi.match(/[\u4e00-\u9fff]/g) ?? []).join('') : hanzi;
    const previousText = React.useRef(suggestionText);
    const controller = React.useRef<AbortController | null>(null);
    const [status, setStatus] = React.useState('Suggested pinyin is editable; please review it.');
    React.useEffect(() => {
        const changed = previousText.current !== suggestionText;
        previousText.current = suggestionText;
        if (changed && latest.current.value) latest.current.onChange('');
        if (!suggestionText.trim() || (!changed && latest.current.value.trim())) return;
        const abort = new AbortController();
        controller.current = abort;
        const original = changed ? '' : latest.current.value;
        if (changed) latest.current.onPendingChange?.(true);
        const timer = setTimeout(() => {
            if (abort.signal.aborted) return;
            setStatus('Suggesting pinyin…');
            SentenceApi.getTextPinyin(suggestionText, abort.signal).then(response => {
                if (abort.signal.aborted || latest.current.value !== original) return;
                latest.current.onChange(response.data.flat().filter(Boolean).join(' '));
                setStatus('Suggested pinyin is editable; please review it.');
            }).catch(() => {
                if (!abort.signal.aborted) setStatus('Could not suggest pinyin. You can enter it manually.');
            }).finally(() => {
                if (!abort.signal.aborted) latest.current.onPendingChange?.(false);
            });
        }, 300);
        return () => { clearTimeout(timer); abort.abort(); latest.current.onPendingChange?.(false); };
    }, [suggestionText]);
    return <TextField label="Pinyin" value={value} helperText={status} fullWidth onChange={event => {
        controller.current?.abort();
        onPendingChange?.(false);
        setStatus('Your pinyin will be saved as entered.');
        onChange(event.target.value);
    }} />;
}
